/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { createObservableDataAction } from '@msdyn365-commerce/action-internal';
import { IAction, IActionContext, ICommerceApiSettings, versionGte } from '@msdyn365-commerce/core-internal';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { getInventoryInStockRefinerAction, InventoryInStockRefinerValueInput } from './get-inventory-in-stock-refiner.action';
import { ProductRefinerValue, ProductSearchCriteria, ProductSearchResult, QueryResultSettings, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {ProductListInput} from '@msdyn365-commerce/runtime-internal/dist/types/hydrators/hydrator-action-inputs';
import { getAttributeInfoAsync } from '../actions/gc-get-attributeInfo';
import { IAttributeInfo } from '../modules/search-result-container/search-result-container';
export declare const CATEGORY_PAGE_NAME = "category";

export const getProductsByCategoryList = async (input: ProductListInput, context: IActionContext) => {
    let categoryId;
    const urlTokens = context.requestContext.urlTokens;
    // const currentPage = context.requestContext.url.requestUrl.href;
    // const originUrl = currentPage && currentPage.endsWith('/') ? `${context.requestContext.url.requestUrl.origin}/` : context.requestContext.url.requestUrl.origin;
    // If the category ID has been provided from the list metadata use it, otherwise grab the category ID from context
    if (input.listMetadata.categoryIds && input.listMetadata.categoryIds.length > 0) {
        categoryId = input.listMetadata.categoryIds[0];
    }
    else if (urlTokens && urlTokens.pageType && urlTokens.pageType.toLowerCase() === CATEGORY_PAGE_NAME && urlTokens.recordId) {
        categoryId = +urlTokens.recordId;
    }
    else if (urlTokens && urlTokens.categories && urlTokens.categories.length > 0) {
        categoryId = +urlTokens.categories[0];
    }
    else {
        // Unable to determine category from list meta data or context
        throw new Error('CategoryId Missing for ProductsByCategory List');
    }
    context.telemetry.debug(`Category Id input to ProductsByCategory action : ${categoryId}`);
    const productSearchCriteria: ProductSearchCriteria  = {
        Context: {
            ChannelId: +input.channelId,
            CatalogId: +input.catalogId
        },
        CategoryIds: [categoryId],
        SkipVariantExpansion: true,
        IncludeAttributes: true
    };
    const queryResultSettings: QueryResultSettings = {
        Paging: {
            Top: input.listMetadata.pageSize || 10
        }
    };
    const rsVersion = !process.env.MSDyn365Commerce_RSVERSION || process.env.MSDyn365Commerce_RSVERSION === '--'
        ? '0.0'
        : process.env.MSDyn365Commerce_RSVERSION;
    const isHideAndSortByInventoryAvailabilitySupported = versionGte(rsVersion, '9.37');
    if (isHideAndSortByInventoryAvailabilitySupported) {
        let channelInventoryConfiguration = {};
        if (context.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS ||
            context.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.SortOOS) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
            const StoreOperationsDataActions = require('@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g');
            channelInventoryConfiguration = await StoreOperationsDataActions.getInventoryConfigurationAsync({ callerContext: context });
        }
        if (context.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS) {
            const inventoryInStockRefinerValueInput = new InventoryInStockRefinerValueInput(context, +input.channelId, channelInventoryConfiguration, +input.catalogId);
            const inventoryInStockRefinerValue = await getInventoryInStockRefinerAction(inventoryInStockRefinerValueInput, context);
            if (inventoryInStockRefinerValue) {
                productSearchCriteria.Refinement = [inventoryInStockRefinerValue];
            }
        }
        if (context.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.SortOOS) {
            const sorting = getInventorySorting(channelInventoryConfiguration);
            if (sorting) {
                queryResultSettings.Sorting = sorting;
            }
        }
    }

    if(context.requestContext.telemetryPageName && context.requestContext.telemetryPageName.toLowerCase() === 'homepage')
    {
        const result = await getAttributeInfoAsync({ callerContext: context }, 'Active');

        const attributeInfo: IAttributeInfo = {};
        attributeInfo.Name = result.gcAttributeInfo?.Name;
        attributeInfo.AttributeGroupId = result.gcAttributeInfo?.AttributeGroupId;
        attributeInfo.DataType = result.gcAttributeInfo?.DataType;
        attributeInfo.LowerBound = result.gcAttributeInfo?.LowerBound;
        attributeInfo.UpperBound = result.gcAttributeInfo?.UpperBound;
        attributeInfo.RecId = result.gcAttributeInfo?.RecId;
        const activeFilter: ProductRefinerValue = {
            DataTypeValue: attributeInfo.DataType,
            LeftValueBoundString: attributeInfo.UpperBound!.toString(),
            RefinerRecordId: attributeInfo.RecId,
            RightValueBoundString: attributeInfo.UpperBound!.toString(),
            ExtensionProperties: [],
            RefinerSourceValue: 1,
            RowNumber: 0,
            SwatchColorHexCode: '',
            SwatchImageUrl: '',
            UnitText: ''
        };
        productSearchCriteria.Refinement = [activeFilter];
    }

    return searchByCriteriaAsync({
        callerContext: context,
        queryResultSettings
    }, productSearchCriteria)
        .then((products) => {
        context.telemetry.debug('Products returned by ProductByCategory action', products);
        return getProductImageUrls(products, context.requestContext.apiSettings);
    })
        .catch(error => {
        context.telemetry.error(`Error running ProductByCategory action: ${error}`);
        throw new Error(error);
    });
};
export const getProductsByCategoryListAction = createObservableDataAction({
    action: <IAction<ProductSearchResult[]>>getProductsByCategoryList,
    id: '@msdyn365-commerce/products-by-category'
});
//# sourceMappingURL=products-by-category.action.js.map

export const getProductImageUrls = (
    products: SimpleProduct[] | ProductSearchResult[],
    apiSettings: ICommerceApiSettings
): SimpleProduct[] | ProductSearchResult[] => {
    const productsWithImageUrls: SimpleProduct[] | ProductSearchResult[] = [];

    products &&
        products.forEach((product: ProductSearchResult | SimpleProduct) => {
            if (product && product.RecordId) {
                product.PrimaryImageUrl = generateImageUrl(product.PrimaryImageUrl, apiSettings);
                productsWithImageUrls.push(product as SimpleProduct);
            }
        });

    return productsWithImageUrls;
};

/**
 * Get inventory sorting value based on channelInventoryConfiguration
 *
 * @param context The current action context
 * @param channelInventoryConfiguration The channelInventoryConfiguration
 */
 export const getInventorySorting = (channelInventoryConfiguration : any) => {
    let sorting;
    if (channelInventoryConfiguration && channelInventoryConfiguration.ProductAvailabilitySortableAttributeRecordId) {
        const sortColumnName = `Attr_${channelInventoryConfiguration.ProductAvailabilitySortableAttributeRecordId}`;
        sorting = {
            Columns: [
                {
                    ColumnName: sortColumnName,
                    IsDescending: true
                }
            ]
        };
    }
    return sorting;
};

/**
 * This setting defines inventory filtering options.
 */
 export enum ProductListInventoryFilteringOptions {
    /**
     * Filter out all products out of stock.
     */
    HideOOS = 'hideOOS',

    /**
     * Sort products by availability, OOS goes last.
     */
    SortOOS = 'sortOOS',

    /**
     * No filtering selected.
     */
    Default = 'default'
}